<template>
  <div></div>
</template>

<script>
import { delayMs } from "vue-chess-shared/utils";
import AiWorker from "@/libs/ai/AiWorker";
import { AI_RANDOM_RATE_BY_LEVEL } from "@/constants";
import Chess from "chess.js";

const MIN_THINKING_TIME = 500;
const MAX_THINKING_TIME = 1000;

export default {
  name: "GamePlayerAi",

  props: {
    color: {
      type: String,
      default: null,
    },

    aiEngine: {
      type: String,
      default: null,
    },

    aiLevel: {
      type: Number,
      default: null,
    },

    lastMove: {
      type: Object,
      default: null,
    },
  },

  inject: ["onPlayerReady", "onAiMakeMove", "onAiAcceptDraw", "onAiDenyDraw"],

  data() {
    return {
      aiWorker: null,
    };
  },

  watch: {
    isReady: {
      handler() {
        if (!this.isReady || this.isLoaded) {
          return;
        }

        this.initWorker();
      },
      immediate: true,
    },

    lastMove() {
      this.checkRequestDraw();
    },
  },

  computed: {
    isReady() {
      return this.aiEngine && this.aiLevel;
    },

    randomRate() {
      const aiLevel = this.aiLevel;
      if (aiLevel < AI_RANDOM_RATE_BY_LEVEL.length) {
        // take the random rate by level
        return AI_RANDOM_RATE_BY_LEVEL[aiLevel];
      } else {
        // higher than all defined levels (100% ai)
        return -1;
      }
    },
  },

  methods: {
    randomMove() {
      let moves = this.engine.moves({ verbose: true });
      return moves[Math.floor(Math.random() * moves.length)];
    },

    // if the level dictates random move should be made, make a random move
    // otherwise let ai engine generates the next move from a pgn
    async nextMove(pgn, initialFen) {
      if (pgn) {
        this.engine.load_pgn(pgn);
      } else {
        this.engine.load(initialFen ?? "");
      }

      if (this.engine.game_over()) {
        return;
      }

      const thinkingTime =
        Math.floor(Math.random() * MAX_THINKING_TIME) + MIN_THINKING_TIME;
      await delayMs(thinkingTime); // give some delay for the viewer to view

      const isEarlyMoves = this.engine.history().length < 10;
      const fen = this.engine.fen();

      if (isEarlyMoves) {
        // a few first moves, default to use ai
        this.aiWorker.generateMove(fen);
        return;
      }

      // valid level & after 10 moves
      // then, random or ai depends on random rate by level
      if (Math.random() < this.randomRate) {
        // falls into the part where a random move is made
        this.onMoveFound(this.randomMove());
        return;
      }

      // falls into the part where an ai move is made
      if (!this.aiWorker || !this.aiWorker.isReady()) {
        // but the worker isn't created or isn't ready, then also made a random move instead
        this.onMoveFound(this.randomMove());
        return;
      }

      // default to use ai move
      this.aiWorker.generateMove(fen);
    },

    onMoveFound(move) {
      if (!move || this.destroyed) {
        console.log("Move generated, but ai is destroyed, so no emit");
        return;
      }

      if (this.onAiMakeMove) this.onAiMakeMove(this.color, move);
    },

    initWorker() {
      this.aiWorker = new AiWorker();
      this.aiWorker.setEngine(this.aiEngine);
      this.aiWorker.onReady = () => {
        this.onPlayerReady(this.color, this);
        this.aiWorker.setDepth(Math.log2(this.aiLevel + 1));

        this.aiWorker.onMoveComplete = ({ bestMove }) => {
          let from, to, promotion;
          if (bestMove) {
            from = bestMove.substring(0, 2);
            to = bestMove.substring(2, 4);
            promotion = bestMove.substring(4, 5);
          } else {
            // AiWorker can't find the next move, so random a move
            const randomMove = this.randomMove();
            from = randomMove.from;
            to = randomMove.to;
            promotion = randomMove.promotion;
          }

          this.onMoveFound({ from, to, promotion });
        };
      };
    },

    checkRequestDraw() {
      // simulate draw response
      const drawRespond =
        Math.random() > 0.5 ? this.onAiAcceptDraw : this.onAiDenyDraw;

      if (!drawRespond) {
        return;
      }

      if (this.requestId) {
        clearTimeout(this.requestId);
      }

      this.requestId = setTimeout(() => {
        drawRespond();
      }, 2000);
    },
  },

  created() {
    this.engine = new Chess();
  },

  beforeDestroy() {
    // to prevent unncessary move generated after stopped
    this.destroyed = true;
    if (this.aiWorker) this.aiWorker.terminate();
  },
};
</script>
